import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { VerseWithMarkers, RefContent } from './VerseWithMarkers';

// Shared by The Bible and the Reading page, so a cross-reference opens the
// same way wherever the reader meets it.

// ── Draggable popup card ─────────────────────────────────────────────────────
// Uses pointer capture for reliable cross-browser drag (no mousemove/mouseup on window).
export const DraggablePopup = React.memo(function DraggablePopup({ popup, idx, onClose, onCloseAll, totalCount, children }) {
  const headerRef = useRef(null);
  const dragState = useRef(null); // { startMX, startMY, startPX, startPY, el }

  function onPointerDown(e) {
    if (e.button !== 0) return;
    // Don't drag when clicking close buttons
    if (e.target.closest('button')) return;
    e.preventDefault();
    const el = headerRef.current?.closest('.bible-ref-popup');
    if (!el) return;
    dragState.current = {
      startMX: e.clientX,
      startMY: e.clientY,
      startPX: popup.x,
      startPY: popup.y,
      el,
    };
    headerRef.current.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e) {
    const ds = dragState.current;
    if (!ds) return;
    const newX = ds.startPX + e.clientX - ds.startMX;
    const newY = ds.startPY + e.clientY - ds.startMY;
    ds.el.style.left = newX + 'px';
    ds.el.style.top  = newY + 'px';
  }

  function onPointerUp(e) {
    const ds = dragState.current;
    if (!ds) return;
    const newX = Math.max(8, ds.startPX + e.clientX - ds.startMX);
    const newY = Math.max(8, ds.startPY + e.clientY - ds.startMY);
    dragState.current = null;
    onClose({ type: 'move', id: popup.id, x: newX, y: newY });
  }

  function onPointerCancel() {
    dragState.current = null;
  }

  return (
    <div className="bible-ref-popup"
      style={{ top: popup.y, left: popup.x, zIndex: 9999 + idx }}
      onClick={e => e.stopPropagation()}
    >
      <div ref={headerRef} className="bible-ref-popup-header"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerCancel}
      >
        <span className="bible-ref-popup-title">{popup.title}</span>
        <div className="bible-ref-popup-actions">
          {totalCount > 1 && (
            <button className="bible-ref-popup-closeall"
              onPointerDown={e => e.stopPropagation()}
              onClick={onCloseAll} title="Close all">✕ all</button>
          )}
          <button className="bible-ref-popup-close"
            onPointerDown={e => e.stopPropagation()}
            onClick={() => onClose({ type: 'close', id: popup.id })}>✕</button>
        </div>
      </div>
      <div className="bible-ref-popup-body">
        {children}
      </div>
    </div>
  );
});

// ── Mobile bottom sheet ──────────────────────────────────────────────────────
// Shown on narrow screens instead of draggable popups.
// history = [{id, kind, title, ...}], idx = current position.
export const MobileBottomSheet = React.memo(function MobileBottomSheet({ history, idx, onNavigate, onClose }) {
  const current = history[idx];
  if (!current) return null;
  const canBack = idx > 0;
  const canFwd  = idx < history.length - 1;
  return (
    <>
      <div className="mobile-sheet-backdrop" onClick={onClose} />
      <div className="mobile-sheet">
        <div className="mobile-sheet-header">
          <div className="mobile-sheet-nav">
            <button className="mobile-sheet-nav-btn" disabled={!canBack}
              onClick={() => onNavigate(idx - 1)} aria-label="Back">‹</button>
            {history.length > 1 && (
              <span className="mobile-sheet-nav-pos">{idx + 1}/{history.length}</span>
            )}
            <button className="mobile-sheet-nav-btn" disabled={!canFwd}
              onClick={() => onNavigate(idx + 1)} aria-label="Forward">›</button>
          </div>
          <span className="mobile-sheet-title">{current.title}</span>
          <button className="mobile-sheet-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="mobile-sheet-body">
          {current.kind === 'note' && (
            <RefContent content={current.content} lang={current.contentLang}
              contextBook={current.contextBook} contextChapter={current.contextChapter} />
          )}
          {current.kind === 'verse' && (
            <div className="bible-ref-verse-stack">
              {current.verses.map(v => (
                <div key={v.verse} className="bible-ref-verse-row">
                  <span className="bible-ref-verse-num">{v.verse}</span>
                  <span className="bible-ref-verse-text">
                    <VerseWithMarkers verseNum={v.verse} plainText={v.text}
                      markedText={current.markedTexts[v.verse]}
                      refsMap={current.refsMap} verseLang={current.verseLang || 'en'} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
});

// ── Popup state ──────────────────────────────────────────────────────────────
// Desktop keeps a cascading stack of draggable cards; a phone keeps one bottom
// sheet with back/forward through what was opened. pushPopup picks between
// them at the moment of the click, so rotating a tablet mid-read still works.
export function useRefPopups() {
  const [popupStack, setPopupStack]   = useState([]); // desktop: [{ id, kind, title, x, y, ... }]
  const [mobileSheet, setMobileSheet] = useState({ history: [], idx: -1 }); // mobile bottom sheet
  const [isMobile, setIsMobile]       = useState(() => window.innerWidth < 768);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Push a note/verse popup — routes to bottom sheet on mobile, draggable stack on desktop
  const pushPopup = useCallback((popup) => {
    if (window.innerWidth < 768) {
      // Mobile: add to sheet history (truncate any forward history first)
      setMobileSheet(prev => {
        const history = [
          ...prev.history.slice(0, prev.idx + 1),
          { id: Date.now() + Math.random(), ...popup },
        ];
        return { history, idx: history.length - 1 };
      });
    } else {
      // Desktop: cascading draggable popups
      setPopupStack(prev => {
        const offset = prev.length * 30;
        const baseX  = popup.x ?? (prev.length ? prev[0].x + offset : 120);
        const baseY  = popup.y ?? (prev.length ? prev[0].y + offset : 120);
        return [...prev, {
          id: Date.now() + Math.random(),
          ...popup,
          x: Math.min(Math.max(baseX, 8), window.innerWidth  - 384),
          y: Math.min(Math.max(baseY, 8), window.innerHeight - 460),
        }];
      });
    }
  }, []);

  const closeAllPopups = useCallback(() => setPopupStack([]), []);
  const closeSheet     = useCallback(() => setMobileSheet({ history: [], idx: -1 }), []);
  const navigateSheet  = useCallback((idx) => setMobileSheet(prev => ({ ...prev, idx })), []);

  // Called by DraggablePopup: either close one popup or commit a drag-move
  const handlePopupAction = useCallback((action) => {
    if (action.type === 'close') {
      setPopupStack(prev => prev.filter(p => p.id !== action.id));
    } else if (action.type === 'move') {
      setPopupStack(prev => prev.map(p => p.id === action.id ? { ...p, x: action.x, y: action.y } : p));
    }
  }, []);

  // Stable context value — prevents all PopupContext consumers re-rendering on unrelated state changes
  const popupContextValue = useMemo(() => ({ pushPopup }), [pushPopup]);

  return {
    popupStack, mobileSheet, isMobile,
    pushPopup, closeAllPopups, closeSheet, navigateSheet, handlePopupAction, popupContextValue,
  };
}

// ── Where the popups draw ────────────────────────────────────────────────────
// Takes the object useRefPopups returns. Must sit inside the page's
// PopupContext.Provider so links inside a popup can open further popups.
export function RefPopupLayer({ popups }) {
  const { isMobile, mobileSheet, popupStack, navigateSheet, closeSheet, handlePopupAction, closeAllPopups } = popups;
  return (
    <>
    {/* ── Mobile bottom sheet (< 768 px) ───────────────────────────── */}
    {isMobile && mobileSheet.idx >= 0 && (
      <MobileBottomSheet
        history={mobileSheet.history}
        idx={mobileSheet.idx}
        onNavigate={navigateSheet}
        onClose={closeSheet}
      />
    )}
    {/* ── Desktop popup stack (≥ 768 px) — fixed, cascaded, draggable ─ */}
    {!isMobile && popupStack.map((popup, idx) => (
      <DraggablePopup key={popup.id} popup={popup} idx={idx}
        onClose={handlePopupAction} onCloseAll={closeAllPopups}
        totalCount={popupStack.length}
      >
        {popup.kind === 'note' && <RefContent content={popup.content} lang={popup.contentLang} contextBook={popup.contextBook} contextChapter={popup.contextChapter} />}
        {popup.kind === 'verse' && (
          <div className="bible-ref-verse-stack">
            {popup.verses.map(v => (
              <div key={v.verse} className="bible-ref-verse-row">
                <span className="bible-ref-verse-num">{v.verse}</span>
                <span className="bible-ref-verse-text">
                  <VerseWithMarkers
                    verseNum={v.verse}
                    plainText={v.text}
                    markedText={popup.markedTexts[v.verse]}
                    refsMap={popup.refsMap}
                    verseLang={popup.verseLang || 'en'}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </DraggablePopup>
    ))}
    </>
  );
}
